import { z } from "zod"
import { branchIdSchema, correlationIdSchema, entityIdSchema, legalEntityIdSchema } from "./identifiers.js"

const base = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
}

export const stockProjectV1Schema = z.object({
  ...base,
  projectionId: entityIdSchema,
  locationId: entityIdSchema,
  itemMasterId: entityIdSchema,
}).strict()

export const stockReservationCreateV1Schema = z.object({
  ...base,
  expectedVersion: z.literal(0),
  expectedStockVersion: z.number().int().nonnegative(),
  reservationId: entityIdSchema,
  revisionId: entityIdSchema,
  locationId: entityIdSchema,
  itemMasterId: entityIdSchema,
  quantityMilli: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  purpose: z.enum(["visit", "transfer", "purchase_request", "manual"]),
  sourceId: entityIdSchema,
  expiresAt: z.string().datetime({ offset: true }).nullable(),
}).strict()

export const stockReservationChangeV1Schema = z.object({
  ...base,
  expectedVersion: z.number().int().positive(),
  expectedStockVersion: z.number().int().nonnegative(),
  reservationId: entityIdSchema,
  revisionId: entityIdSchema,
  action: z.enum(["release", "expire"]),
  reasonCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
}).strict()

export const stockProjectionResultV1Schema = z.object({
  entityType: z.literal("stock_projection"),
  entityId: entityIdSchema,
  state: z.literal("projected"),
  version: z.number().int().nonnegative(),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  locationId: entityIdSchema,
  itemMasterId: entityIdSchema,
  warehouse: z.string().min(1).max(240),
  itemCode: z.string().min(1).max(240),
  stockUom: z.string().min(1).max(140),
  actualMilli: z.number().int(),
  reservedMilli: z.number().int().nonnegative(),
  availableMilli: z.number().int(),
  orderedMilli: z.number().int(),
  requestedMilli: z.number().int(),
  projectedMilli: z.number().int(),
  stockVersion: z.number().int().nonnegative(),
  authoritativeNegativeStockAllowed: z.literal(false),
}).strict().refine(
  (value) => value.availableMilli === value.actualMilli - value.reservedMilli,
  { message: "Available quantity must equal actual minus active reservations." },
)

export const stockReservationResultV1Schema = z.object({
  entityType: z.literal("stock_reservation"),
  entityId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  locationId: entityIdSchema,
  itemMasterId: entityIdSchema,
  state: z.enum(["active", "released", "expired"]),
  version: z.number().int().positive(),
  stockVersion: z.number().int().positive(),
  quantityMilli: z.number().int().positive(),
  availableAfterMilli: z.number().int(),
}).strict()

export type StockProjectV1 = z.infer<typeof stockProjectV1Schema>
export type StockReservationCreateV1 = z.infer<typeof stockReservationCreateV1Schema>
export type StockReservationChangeV1 = z.infer<typeof stockReservationChangeV1Schema>
